// Citation data layer for the "click a citation -> jump to the source PDF"
// feature. Loads the grounded full_summary.json a summary produced (every
// point carries its verbatim source quote + page attribution) and resolves
// each filer to the PDF we already hold in storage.
import { supabase, STORAGE_BUCKET } from './supabase';

export interface SourceQuote {
  text: string;
  page: number | null;
}

export interface CitationRef {
  id: string; // stable key for active-citation tracking
  topic: string;
  entity: string;
  point: string;
  sourceText: string;
  page: number | null;
  pageRanges: number[];
  /** Additional grounded passages on the other cited pages. Together with
   * sourceText these are highlighted across the document. */
  additionalQuotes: SourceQuote[];
}

export interface CitedEntity {
  entity: string;
  points: CitationRef[];
}

export interface CitedTopic {
  topic: string;
  entities: CitedEntity[];
}

// Folders the ingest pipeline writes filing PDFs into, keyed by ECFS
// submissiontype short code. Scanned in order; the first match for a filer
// wins — so the segment order must match the summary's filing type, since a
// filer often submits in BOTH a comment and a reply round.
const COMMENT_SEGMENTS = ['COMMENT', 'EXPRESS-COMMENT'];
const REPLY_SEGMENTS = ['REPLY_COMM', 'REPLY'];
const ALL_SEGMENTS = [...COMMENT_SEGMENTS, ...REPLY_SEGMENTS];

/** Storage folders to resolve a filer's PDF from, scoped to the summary's
 * filing round so a reply summary doesn't pick up the filer's initial
 * comment (or vice versa). */
export function segmentsForSummary(periodType?: string | null, filingType?: string | null): string[] {
  const pt = (periodType || '').toLowerCase();
  const ft = (filingType || '').toUpperCase();
  if (pt === 'reply_comments' || ft.startsWith('REPLY')) return REPLY_SEGMENTS;
  if (pt === 'comments' || ft.includes('COMMENT')) return COMMENT_SEGMENTS;
  return ALL_SEGMENTS;
}

function normName(s: string): string {
  return (s || '')
    .toLowerCase()
    .replace(/[^a-z0-9 ]+/g, ' ')
    .replace(/\b(inc|llc|corp|corporation|company|co|ltd|lp|the|of|and)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Map a parsed full_summary.json object into topic -> entity -> cited
 * points. Pure (no IO) so it can run on a storage download OR on a payload
 * already loaded in memory (e.g. the shared-summary page). */
export function mapFullSummaryToTopics(parsed: any): CitedTopic[] {
  const topics: CitedTopic[] = [];
  for (const t of parsed?.summaries ?? []) {
    const entities: CitedEntity[] = [];
    for (const ent of t.summaries_by_topic ?? []) {
      const points: CitationRef[] = [];
      (ent.individual_summary ?? []).forEach((p: any, i: number) => {
        const pa = p.page_attribution ?? {};
        const point = (p.point ?? '').trim();
        const sourceText = (p.source_text ?? '').trim();
        if (!point) return;
        const additionalQuotes: SourceQuote[] = Array.isArray(p.additional_source_texts)
          ? p.additional_source_texts
              .map((q: any) => ({
                text: (q?.text ?? '').trim(),
                page: typeof q?.page === 'number' ? q.page : null,
              }))
              .filter((q: SourceQuote) => q.text.length > 0)
          : [];
        points.push({
          id: `${t.topic}::${ent.entity}::${i}`,
          topic: t.topic,
          entity: ent.entity,
          point,
          sourceText,
          page: typeof pa.primary_page === 'number' ? pa.primary_page : null,
          pageRanges: Array.isArray(pa.page_ranges) ? pa.page_ranges : [],
          additionalQuotes,
        });
      });
      if (points.length) entities.push({ entity: ent.entity, points });
    }
    if (entities.length) topics.push({ topic: t.topic, entities });
  }
  return topics;
}

/** Download + parse full_summary.json into cited topics. */
export async function loadCitedSummary(storagePath: string): Promise<CitedTopic[]> {
  const path = `${storagePath}/full_summary.json`;
  const { data, error } = await supabase.storage.from(STORAGE_BUCKET).download(path);
  if (error || !data) {
    throw new Error(`Could not load citations (${error?.message ?? 'no data'})`);
  }
  return mapFullSummaryToTopics(JSON.parse(await data.text()));
}

// ---------------------------------------------------------------------------
// Filer -> PDF resolution. Storage filenames are `<sid>___<filer>___<title>`,
// so we index the filer segment of every PDF in the docket's comment/reply
// folders and fuzzy-match the summary's entity name to it.
// ---------------------------------------------------------------------------
interface FilerEntry {
  norm: string;
  path: string;
  fileName: string;
}

const _indexCache = new Map<string, FilerEntry[]>();

async function buildFilerIndex(docket: string, segments: string[]): Promise<FilerEntry[]> {
  const key = `${docket}|${segments.join(',')}`;
  const cached = _indexCache.get(key);
  if (cached) return cached;
  const entries: FilerEntry[] = [];
  for (const seg of segments) {
    const prefix = `ecfs/${docket}/${seg}`;
    const { data, error } = await supabase.storage
      .from(STORAGE_BUCKET)
      .list(prefix, { limit: 1000 });
    if (error || !data) continue;
    for (const obj of data) {
      const name = obj.name;
      if (!name || !name.toLowerCase().endsWith('.pdf')) continue;
      const parts = name.split('___');
      const filer = parts.length >= 3 ? parts[1] : name;
      entries.push({
        norm: normName(filer.replace(/_/g, ' ')),
        path: `${prefix}/${name}`,
        fileName: name,
      });
    }
  }
  _indexCache.set(key, entries);
  return entries;
}

/** Resolve a summary entity name to the best-matching PDF in storage,
 * scoped to the given filing-round folders (defaults to all). */
export async function resolveFilerPdfPath(
  docket: string,
  entity: string,
  segments: string[] = ALL_SEGMENTS,
): Promise<string | null> {
  const index = await buildFilerIndex(docket, segments);
  if (!index.length) return null;
  const ne = normName(entity);
  // Exact / containment match first.
  for (const e of index) {
    if (e.norm && (e.norm === ne || ne.includes(e.norm) || e.norm.includes(ne))) {
      return e.path;
    }
  }
  // Token-overlap fallback.
  const et = new Set(ne.split(' ').filter(Boolean));
  let best: FilerEntry | null = null;
  let bestScore = 0;
  for (const e of index) {
    const overlap = e.norm.split(' ').filter((w) => et.has(w)).length;
    if (overlap > bestScore) {
      bestScore = overlap;
      best = e;
    }
  }
  return bestScore > 0 && best ? best.path : null;
}

/** Download a PDF from storage as bytes for pdf.js. */
const _pdfBytesCache = new Map<string, ArrayBuffer>();
export async function downloadPdfBytes(path: string): Promise<ArrayBuffer> {
  const cached = _pdfBytesCache.get(path);
  if (cached) return cached;
  const { data, error } = await supabase.storage.from(STORAGE_BUCKET).download(path);
  if (error || !data) throw new Error(`Could not download PDF (${error?.message ?? 'no data'})`);
  const buf = await data.arrayBuffer();
  _pdfBytesCache.set(path, buf);
  return buf;
}
